/**
 * Drug data the medication form reads (#692): the registry's — or an
 * unlinked Warehouse Product's — strength and pharmaceutical form as the
 * form's own fields, and the Warehouse Unit a dose of each form is counted
 * in. Hook-free, so the form's tests reach it without the organization
 * context.
 */
import {
  GetProfileMedicationsDtoFormEnum,
  GetProfileMedicationsDtoStrengthUnitEnum,
  WarehouseUnit,
} from 'woofwoof-api';

export interface StrengthPrefill {
  strengthValue: number;
  strengthUnit: GetProfileMedicationsDtoStrengthUnitEnum;
  strengthUnitCustom: string;
}

export interface FormPrefill {
  form: GetProfileMedicationsDtoFormEnum;
  formCustom: string;
}

interface FormRule {
  pattern: RegExp;
  form: GetProfileMedicationsDtoFormEnum;
}

/** A leading amount — "5", "1,5", "20 000" (grouped thousands) — then the rest. */
const LEADING_AMOUNT = /^(\d{1,3}(?:[  ]\d{3})+|\d+)(?:[,.](\d+))?\s*(.*)$/;

const GROUP_SEPARATOR = /[  ]/g;

const STRENGTH_UNITS: Record<string, GetProfileMedicationsDtoStrengthUnitEnum> =
  {
    mg: 'MG',
    g: 'G',
    mcg: 'MCG',
    µg: 'MCG',
    μg: 'MCG',
    ml: 'ML',
    iu: 'IU',
    'j.m.': 'IU',
    '%': 'PERCENT',
  };

/**
 * First match wins, so an injection is an injection whatever it is made
 * from ("Proszek i rozpuszczalnik … do wstrzykiwań"), and the head noun
 * decides the rest ("Proszek do inhalacji w kapsułkach" is a powder).
 */
const FORM_RULES: FormRule[] = [
  { pattern: /wstrzykiw/, form: 'INJECTION' },
  { pattern: /nakrapia|nakrapla|spot-on/, form: 'SPOT_ON' },
  { pattern: /^tabletk/, form: 'TABLET' },
  { pattern: /^kapsułk/, form: 'CAPSULE' },
  { pattern: /^saszetk/, form: 'SACHET' },
  { pattern: /^zawiesin/, form: 'SUSPENSION' },
  { pattern: /^roztw[óo]r/, form: 'SOLUTION' },
  { pattern: /^krople/, form: 'DROPS' },
  { pattern: /^maść/, form: 'OINTMENT' },
  { pattern: /^(aerozol|spray)/, form: 'SPRAY' },
  { pattern: /^proszek/, form: 'POWDER' },
  { pattern: /^pasta/, form: 'PASTE' },
];

/**
 * The form has no ampoule or vial: a liquid — an injection included — is
 * dosed in ml, a spot-on by the pipette.
 */
const DOSE_UNITS: Partial<
  Record<GetProfileMedicationsDtoFormEnum, WarehouseUnit>
> = {
  TABLET: 'TABLET',
  CAPSULE: 'CAPSULE',
  SACHET: 'SACHET',
  SOLUTION: 'ML',
  SUSPENSION: 'ML',
  INJECTION: 'ML',
  SPOT_ON: 'PIPETTE',
};

/**
 * "5 mg" → 5 + MG. "5 mg/ml", "200 mg + 50 mg" → the amount + OTHER with the
 * rest as written, so the form still reads "5 mg/ml". Nothing for a strength
 * without a leading amount ("(50 mg + 60 mg)/0,5 ml") or without a unit.
 */
export function parseDrugStrength(
  text?: string | null,
): StrengthPrefill | null {
  const match = LEADING_AMOUNT.exec(text?.trim() ?? '');
  if (!match) return null;
  const [, whole, fraction, rest] = match;
  const unitText = rest.trim();
  const strengthValue = Number(
    `${whole.replace(GROUP_SEPARATOR, '')}${fraction ? `.${fraction}` : ''}`,
  );
  if (!unitText || !Number.isFinite(strengthValue)) return null;
  const strengthUnit = STRENGTH_UNITS[unitText.toLowerCase()];
  return strengthUnit
    ? { strengthValue, strengthUnit, strengthUnitCustom: '' }
    : { strengthValue, strengthUnit: 'OTHER', strengthUnitCustom: unitText };
}

/**
 * The registry's pharmaceutical form as the form's own list ("Tabletki
 * powlekane" → TABLET, "Roztwór do wstrzykiwań" → INJECTION). A form the
 * list has no entry for is kept as written under "Inne".
 */
export function mapDrugForm(text?: string | null): FormPrefill | null {
  const written = text?.trim();
  if (!written) return null;
  const lower = written.toLowerCase();
  const rule = FORM_RULES.find(({ pattern }) => pattern.test(lower));
  return rule
    ? { form: rule.form, formCustom: '' }
    : { form: 'OTHER', formCustom: written };
}

/** The Warehouse Unit a dose of this form is counted in, when the form says. */
export function stockUnitForDoseForm(
  form: GetProfileMedicationsDtoFormEnum | '',
): WarehouseUnit | null {
  return (form && DOSE_UNITS[form]) || null;
}
